import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Circle, Check, X, Users, Shield, Coins, Trophy } from "lucide-react";

function StatBar({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-numeric text-stone-500">
      {label}: <span className="text-amber-300">{value}</span>
    </span>
  );
}

function FriendRow({ friend, action, onAction, testid }) {
  return (
    <div
      className="panel-inset p-2.5 flex items-center gap-3 transition-colors"
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-full bg-stone-950 border-2 border-amber-800/70 flex items-center justify-center font-display text-amber-400 shrink-0">
        {(friend.name || "?").slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-display text-sm text-stone-100 truncate">{friend.name}</span>
          {typeof friend.online !== "undefined" && (
            <span
              className={
                "inline-flex items-center gap-1 text-[9px] font-numeric uppercase tracking-widest " +
                (friend.online ? "text-emerald-400" : "text-stone-500")
              }
            >
              <Circle size={7} className={friend.online ? "fill-emerald-400" : "fill-stone-600"} strokeWidth={0} />
              {friend.online ? "Online" : "Offline"}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3 mt-0.5">
          <StatBar label="LV" value={friend.total_level} />
          <StatBar label="XP" value={(friend.total_xp || 0).toLocaleString()} />
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">{action}</div>
    </div>
  );
}

export function FriendsPanel() {
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/social/friends");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/game/social/friends/request", { name: name.trim() });
      toast.success(data.accepted ? "Friend request accepted!" : "Friend request sent.");
      setName("");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function respond(user_id, accept) {
    try {
      await api.post("/api/game/social/friends/respond", { user_id, accept });
      toast.message(accept ? "Friend added." : "Request declined.");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function unfriend(user_id) {
    try {
      await api.post("/api/game/social/friends/remove", { user_id });
      toast.message("Friend removed.");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="friends-panel">
      {/* Portrait band */}
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-20 h-20 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <Users size={34} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl heading-gilded">Friends</h1>
            <p className="text-stone-400 font-body italic text-sm">Wanderers you keep company with, across worlds.</p>
            <div className="flex flex-wrap gap-4 mt-2 text-sm font-numeric text-stone-300">
              <span>Friends · <span className="text-amber-300">{data.friends.length}</span></span>
              <span>Incoming · <span className="text-amber-300">{data.incoming.length}</span></span>
              <span>Sent · <span className="text-amber-300">{data.outgoing.length}</span></span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Add friend */}
        <section className="panel p-3 lg:col-span-1 space-y-2">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">Add a friend</h3>
          <form onSubmit={submit} className="flex gap-2" data-testid="add-friend-form">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Character name…"
              maxLength={40}
              className="flex-1 bg-stone-950 border border-stone-700 rounded px-3 py-2 font-body text-sm focus:border-amber-600 outline-none"
              data-testid="friend-name-input"
            />
            <button type="submit" disabled={busy} className="btn-gold px-3" data-testid="friend-send-btn">
              <UserPlus size={14} />
            </button>
          </form>
          <p className="text-[11px] font-body text-stone-500 italic">
            Send a request by the wanderer&apos;s character name. If they&apos;ve already asked you, this will accept.
          </p>

          {data.incoming.length > 0 && (
            <div className="mt-3">
              <h4 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-1.5">Incoming</h4>
              <div className="space-y-1.5">
                {data.incoming.map((f) => (
                  <FriendRow
                    key={f.id}
                    friend={f}
                    testid={`incoming-${f.id}`}
                    action={
                      <>
                        <button
                          onClick={() => respond(f.id, true)}
                          className="p-1.5 rounded bg-emerald-800 hover:bg-emerald-700 text-emerald-50 border border-emerald-600"
                          data-testid={`accept-${f.id}`}
                          title="Accept"
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={() => respond(f.id, false)}
                          className="p-1.5 rounded bg-stone-800 hover:bg-stone-700 text-stone-300 border border-stone-600"
                          data-testid={`decline-${f.id}`}
                          title="Decline"
                        >
                          <X size={12} />
                        </button>
                      </>
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {data.outgoing.length > 0 && (
            <div className="mt-3">
              <h4 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-1.5">Sent</h4>
              <div className="space-y-1.5">
                {data.outgoing.map((f) => (
                  <FriendRow
                    key={f.id}
                    friend={f}
                    testid={`outgoing-${f.id}`}
                    action={
                      <span className="text-[10px] font-numeric text-stone-500 italic">Pending…</span>
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Friends list */}
        <section className="panel p-3 lg:col-span-2">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500 mb-2">
            Your circle ({data.friends.length})
          </h3>
          {data.friends.length === 0 ? (
            <div className="text-stone-500 italic text-sm font-body py-6 text-center">
              You wander alone. Add a friend to see when they come online.
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="friends-list">
              {data.friends.map((f) => (
                <FriendRow
                  key={f.id}
                  friend={f}
                  testid={`friend-${f.id}`}
                  action={
                    <button
                      onClick={() => unfriend(f.id)}
                      className="text-[10px] px-2 py-1 rounded border border-stone-700 bg-stone-900 text-stone-400 hover:text-red-300 hover:border-red-700 font-body"
                      data-testid={`unfriend-${f.id}`}
                    >
                      Remove
                    </button>
                  }
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
